import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useMemo, useState } from 'react';
import {
  Credenza,
  CredenzaBody,
  CredenzaContent,
  CredenzaDescription,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
  CredenzaTrigger,
} from '@/components/ui/credenza.tsx';
import { Button } from '@/components/ui/button.tsx';
import { SquarePenIcon } from 'lucide-react';
import { Form } from '@/components/ui/form.tsx';
import { ExpandedState, Row, RowData, RowSelectionState } from '@tanstack/react-table';
import {
  collectDeviceSns,
  createDefaultDevice,
  createDeviceSchemaWithSnCheck,
  TDevice,
  TTreeDevice,
} from '@/features/devices/schemas/devices.schemas.ts';
import { useTranslation } from 'react-i18next';
import {
  computeExpandedStateAfterMove,
  flattenDevicesWithPath,
  getDeviceAtPath,
  getParentPath,
  moveDevice,
  parseRowPath,
  updateDeviceAtPath,
} from '@/features/devices/components/device-tree-table/device-tree-table.utils.ts';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs.tsx';
import { DeviceEditDetailsTabForm } from '@/features/devices/components/device-tree-table/device-edit-details-tab-form.tsx';
import { DeviceEditMoveTabForm } from '@/features/devices/components/device-tree-table/device-edit-move-tab-form.tsx';

const ROOT_PATH = '__root__';

export const DeviceTableToolbarEditButton = ({
  data,
  deviceInfo,
  hasSelectedRow,
  getSelectedTableRow,
  getSelectedDevice,
  setData,
  setExpandedRows,
  setSelectedRows,
}: {
  data: TTreeDevice[];
  deviceInfo: {
    plantId: string;
    timezone: string;
  };
  hasSelectedRow: boolean;
  getSelectedTableRow: () => Row<RowData> | undefined;
  getSelectedDevice: () => TTreeDevice | null;
  setData: (devices: TTreeDevice[]) => void;
  setExpandedRows: React.Dispatch<React.SetStateAction<ExpandedState>>;
  setSelectedRows: React.Dispatch<React.SetStateAction<RowSelectionState>>;
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [selectedParentPath, setSelectedParentPath] = useState<string>(ROOT_PATH);
  const { t } = useTranslation();
  const selectedDevice = getSelectedDevice();
  const schema = useMemo(
    () => createDeviceSchemaWithSnCheck(collectDeviceSns(data), selectedDevice?.sn),
    [data, selectedDevice?.sn]
  );
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: createDefaultDevice(deviceInfo),
  });

  const selectedRow = getSelectedTableRow();
  const currentRowPath = selectedRow?.id;

  const currentParentPath = useMemo(() => {
    if (!currentRowPath) return ROOT_PATH;
    return getParentPath(currentRowPath) ?? ROOT_PATH;
  }, [currentRowPath]);

  const parentOptions = useMemo(() => {
    if (!currentRowPath) return [];
    return flattenDevicesWithPath(data, parseRowPath(currentRowPath));
  }, [data, currentRowPath]);

  const handleDeviceUpdate = (formData: TDevice) => {
    if (!selectedRow || !currentRowPath) return;

    const sourcePath = parseRowPath(currentRowPath);

    if (selectedParentPath === currentParentPath) {
      setData(
        updateDeviceAtPath(data, sourcePath, (device) => ({
          ...formData,
          subDevices: device.subDevices,
        }))
      );
      setSelectedRows({});
      setIsDialogOpen(false);
      return;
    }

    const originalDevice = getDeviceAtPath(data, sourcePath);
    if (!originalDevice) return;

    const movedDevice: TTreeDevice = { ...formData, subDevices: originalDevice.subDevices };
    const targetParentPath =
      selectedParentPath === ROOT_PATH ? null : parseRowPath(selectedParentPath);

    const {
      devices: newData,
      newPath,
      adjustedTargetParent,
    } = moveDevice(data, sourcePath, targetParentPath, movedDevice);

    setData(newData);
    setSelectedRows({});
    setExpandedRows((prev) => {
      if (prev === true) return true;
      return computeExpandedStateAfterMove(
        prev,
        sourcePath,
        newPath,
        adjustedTargetParent ?? undefined
      );
    });
    setIsDialogOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    setIsDialogOpen(open);
    if (open && selectedDevice) {
      form.reset(selectedDevice);
      setSelectedParentPath(currentParentPath);
      setActiveTab('details');
    }
  };

  return (
    <Credenza open={isDialogOpen} onOpenChange={handleOpenChange}>
      <CredenzaTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          className="flex items-center gap-2 rounded-none px-2 py-5 font-normal hover:bg-gray-200 sm:px-3"
          disabled={!hasSelectedRow}
        >
          <SquarePenIcon className="text-muted-foreground size-4" />
          <span>{t('common.buttons.editButton')}</span>
        </Button>
      </CredenzaTrigger>
      <CredenzaContent>
        <Form {...form}>
          <CredenzaHeader>
            <CredenzaTitle>{t('devices.actions.editDevice')}</CredenzaTitle>
          </CredenzaHeader>
          <CredenzaBody>
            <CredenzaDescription className="hidden">
              {t('common.buttons.editAll')}
            </CredenzaDescription>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="w-full">
                <TabsTrigger value="details" className="flex-1">
                  {t('devices.actions.detailsTab')}
                </TabsTrigger>
                <TabsTrigger value="location" className="flex-1">
                  {t('devices.actions.locationTab')}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="details">
                <DeviceEditDetailsTabForm />
              </TabsContent>

              <TabsContent value="location">
                <DeviceEditMoveTabForm
                  parentOptions={parentOptions}
                  selectedParentPath={selectedParentPath}
                  onParentPathChange={setSelectedParentPath}
                />
              </TabsContent>
            </Tabs>
          </CredenzaBody>
          <CredenzaFooter>
            <Button type="button" variant="ghost" onClick={() => setIsDialogOpen(false)}>
              {t('common.buttons.cancelButton')}
            </Button>
            <Button
              type="button"
              onClick={form.handleSubmit(handleDeviceUpdate, () => setActiveTab('details'))}
            >
              {t('common.buttons.saveChangesButton')}
            </Button>
          </CredenzaFooter>
        </Form>
      </CredenzaContent>
    </Credenza>
  );
};
